import { ArrowDownToLine, ArrowUpFromLine, Wallet } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { resolvePocketName } from "../constants/pockets.constants";
import {
  invalidateOrganizationWalletBalance,
  useOrganizationWalletBalance,
} from "../hooks/useOrganizationWalletBalance";
import { PocketsService, type PocketTransferType } from "../services/pocketsService";
import type { Pocket } from "../types/pockets-api.types";
import { PocketIcon } from "./PocketIcon";

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000] as const;

const formatUSD = (raw: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

const parseAmount = (raw: number | string | null | undefined): number => {
  if (raw == null) return 0;
  if (typeof raw === "number") return raw;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

type LoadPocketModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pocket: Pocket | null;
  onSuccess?: () => void;
  initialMode?: PocketTransferType;
};

export const LoadPocketModal = ({
  open,
  onOpenChange,
  pocket,
  onSuccess,
  initialMode = "topup",
}: LoadPocketModalProps) => {
  const { t } = useTranslations("cards");
  const { user } = useAuth();
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;

  const [mode, setMode] = useState<PocketTransferType>(initialMode);
  const [amount, setAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { balance: walletBalance, isLoading: isLoadingWallet } = useOrganizationWalletBalance();
  const walletAvailable = walletBalance ? parseAmount(walletBalance.current.balance) : null;

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setAmount("");
    }
  }, [open, initialMode, pocket?.id]);

  const pocketBalance = parseAmount(pocket?.balance);
  const cap = mode === "topup" ? (walletAvailable ?? Infinity) : pocketBalance;
  const parsedAmount = parseAmount(amount);
  const amountValid = parsedAmount > 0 && parsedAmount <= cap;

  const ctaLabelKey = mode === "topup" ? "pocket_load_submit_topup" : "pocket_load_submit_withdraw";

  const handleSubmit = async () => {
    if (!pocket || !amountValid) return;
    setIsSubmitting(true);
    try {
      const response = await PocketsService.transferPocket(pocket.id, {
        type: mode,
        amount: parsedAmount,
        currency: "USD",
      });

      // Provider moved funds but our COMPLETED promotion failed — row shows
      // PENDING until the retry queue catches up.
      if (response.data?.accounting_status === "pending_reconciliation") {
        colocaToast.info(t("pocket_load_pending_reconciliation"));
      } else {
        colocaToast.success(
          t(mode === "topup" ? "pocket_load_success_topup" : "pocket_load_success_withdraw", {
            amount: formatUSD(parsedAmount),
          }),
        );
      }
      // No webhook for pocket transfers — cached balance lags ~10s for the
      // debounced sync.
      colocaToast.info(t("pocket_load_balance_processing_hint"));
      if (organizationId) invalidateOrganizationWalletBalance(organizationId);
      onSuccess?.();
      onOpenChange(false);
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("pocket_load_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const pocketName = pocket ? resolvePocketName(pocket) : "";
  const submitDisabled = !pocket || !amountValid || isSubmitting;

  const hintLabel = useMemo(() => {
    if (mode === "topup") {
      if (isLoadingWallet) return t("pocket_load_wallet_loading");
      if (walletAvailable == null) return null;
      return t("pocket_load_wallet_available", {
        amount: formatUSD(walletAvailable),
      });
    }
    return t("pocket_load_pocket_available", {
      amount: formatUSD(pocketBalance),
    });
  }, [mode, isLoadingWallet, walletAvailable, pocketBalance, t]);

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("pocket_load_modal_title")}
      description={t("pocket_load_modal_description")}
      size="default"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <ColocaButton variant="ghost" onClick={() => onOpenChange(false)}>
            {t("create_identity_modal_button_cancel")}
          </ColocaButton>
          <ColocaButton
            variant="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={submitDisabled}
          >
            {t(ctaLabelKey as never)}
          </ColocaButton>
        </div>
      }
    >
      <div className="space-y-5">
        {pocket && (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <PocketIcon icon={pocket.metadata?.icon} color={pocket.metadata?.color} size={36} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">{pocketName}</div>
              <div className="text-[11px] text-muted-foreground">
                {t("pocket_load_pocket_balance_label")}:{" "}
                <span className="font-mono">{formatUSD(pocketBalance)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMode("topup")}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
              mode === "topup"
                ? "border-primary bg-primary/5 text-foreground"
                : "border-border bg-card text-muted-foreground hover:bg-muted/40",
            )}
          >
            <ArrowDownToLine className="h-3.5 w-3.5" />
            {t("pocket_load_mode_topup")}
          </button>
          <button
            type="button"
            onClick={() => setMode("withdrawal")}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
              mode === "withdrawal"
                ? "border-primary bg-primary/5 text-foreground"
                : "border-border bg-card text-muted-foreground hover:bg-muted/40",
            )}
          >
            <ArrowUpFromLine className="h-3.5 w-3.5" />
            {t("pocket_load_mode_withdraw")}
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <label htmlFor="pocket-load-amount" className="text-sm font-medium text-foreground">
              {t("balance_modal_amount_label")}
            </label>
            {hintLabel && (
              <span className="text-[11px] text-muted-foreground">
                <Wallet className="mr-1 inline h-3 w-3" />
                {hintLabel}
              </span>
            )}
          </div>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <input
              id="pocket-load-amount"
              type="number"
              inputMode="decimal"
              min={0}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="h-11 w-full rounded-md border border-border bg-background pl-7 pr-12 font-mono text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              USD
            </span>
          </div>
          {parsedAmount > 0 && parsedAmount > cap && Number.isFinite(cap) && (
            <p className="text-[11px] text-destructive">
              {mode === "topup" ? t("balance_modal_amount_over_wallet") : t("balance_modal_amount_over_card")}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {PRESET_AMOUNTS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setAmount(String(preset))}
              className={cn(
                "h-7 rounded-full border border-border bg-card px-2.5 text-xs text-muted-foreground transition-colors hover:border-border/80 hover:text-foreground",
                String(preset) === amount && "border-primary bg-primary/5 text-foreground",
              )}
            >
              +{formatUSD(preset)}
            </button>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground">{t("balance_modal_amount_helper")}</p>
      </div>
    </ColocaDialog>
  );
};
